import React, { useState, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { FixedSizeList as List } from "react-window";
import { useDebounce } from "use-debounce";

// Define TypeScript interfaces for better type safety
interface Branch {
  value: number;
  label: string;
  NameAlias?: string;
}

interface BranchSelectorProps {
  branches: Branch[];
  name: string;
  label: string;
}

const BranchSelector: React.FC<BranchSelectorProps> = ({
  branches,
  name,
  label,
}) => {
  const { setValue, watch } = useFormContext();
  const selectedValues = watch(name) || [];
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300); // Debounce search by 300ms

  // Ensure selectedValues is always an array
  useEffect(() => {
    if (!Array.isArray(selectedValues)) {
      setValue(name, [], { shouldDirty: true, shouldTouch: true });
    }
  }, [name, setValue, selectedValues]);

  // Validate for duplicate values (for debugging)
  useEffect(() => {
    const valueSet = new Set(branches.map((branch) => branch.value));
    if (valueSet.size !== branches.length) {
      console.warn("Duplicate branch values detected:", branches);
    }
  }, [branches]);

  // Determine if the current theme is dark
  const isDarkTheme =
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  const checkboxBorderColor = isDarkTheme ? "#ffffff" : "#333333";

  // Handle "Select All" checkbox
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allValues = branches.map((branch) => branch.value);
      setValue(name, allValues, { shouldDirty: true, shouldTouch: true });
    } else {
      setValue(name, [], { shouldDirty: true, shouldTouch: true });
    }
  };

  const isAllSelected =
    branches.length > 0 &&
    Array.isArray(selectedValues) &&
    selectedValues.length === branches.length &&
    branches.every((branch) => selectedValues.includes(branch.value));

  // Filter and sort branches: checked items at the top
  const filteredBranches = useMemo(() => {
    // Filter based on debounced search term
    let filtered = branches;
    if (debouncedSearchTerm) {
      const lowerSearch = debouncedSearchTerm.toLowerCase();
      filtered = branches.filter((branch) =>
        branch?.label?.toLowerCase().includes(lowerSearch)
      );
    }

    // Sort: checked items (in selectedValues) first, then unchecked
    return filtered.sort((a, b) => {
      const aChecked = selectedValues.includes(a.value);
      const bChecked = selectedValues.includes(b.value);
      if (aChecked && !bChecked) return -1; // a is checked, b is not
      if (!aChecked && bChecked) return 1; // b is checked, a is not
      return 0; // Preserve original order within checked/unchecked groups
    });
  }, [branches, debouncedSearchTerm, selectedValues]);

  // Handle individual checkbox changes
  const handleCheckboxChange = (branchValue: number, checked: boolean) => {
    let newValue: number[];
    if (checked) {
      newValue = Array.isArray(selectedValues)
        ? [...new Set([...selectedValues, branchValue])]
        : [branchValue];
    } else {
      newValue = Array.isArray(selectedValues)
        ? selectedValues.filter((val: number) => val !== branchValue)
        : [];
    }
    setValue(name, newValue, { shouldDirty: true, shouldTouch: true });
  };

  // Render a single branch item for react-window
  const Row = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    const branch = filteredBranches[index];
    if (!branch) return null;

    return (
      <div style={style} className="form-check mb-2">
        <input
          type="checkbox"
          id={`branch-${name}-${branch.value}`}
          value={branch.value}
          checked={selectedValues.includes(branch.value)}
          onChange={(e) =>
            handleCheckboxChange(Number(branch.value), e.target.checked)
          }
          className="form-check-input h-5 w-5 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
          style={{
            border: `2px solid ${checkboxBorderColor}`,
            borderRadius: "4px",
            cursor: "pointer",
          }}
        />
        <label
          htmlFor={`branch-${name}-${branch.value}`}
          className="form-check-label ms-2 text-gray-700"
        >
          {branch.label}
        </label>
      </div>
    );
  };

  return (
    <div className="mt-1 relative mb-2">
      <div className="pt-2">
        <div className="form-check mb-1">
          <input
            type="checkbox"
            id={`selectAll-${name}`}
            className="form-check-input h-5 w-5 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
            checked={isAllSelected}
            onChange={handleSelectAll}
            style={{
              border: `2px solid ${checkboxBorderColor}`,
              borderRadius: "4px",
              cursor: "pointer",
            }}
          />
          <label
            htmlFor={`selectAll-${name}`}
            className="form-check-label fw-bold ms-2 text-gray-800"
          >
            All {label} ({branches.length})
          </label>
        </div>
        <div className="mb-1">
          <input
            type="text"
            placeholder={`Search ${label}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-control"
          />
        </div>

        <div
          className="border rounded-lg p-3 shadow-sm"
          style={{ height: "16rem", overflow: "hidden" }}
        >
          {filteredBranches.length > 0 ? (
            <List
              height={237}
              itemCount={filteredBranches.length}
              itemSize={40}
              width="100%"
            >
              {Row}
            </List>
          ) : (
            <p className="text-gray-500 text-center">No {label} found</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BranchSelector;
